import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { Button, Col, Form, Modal, Row, Select } from 'antd';

import { ChevronDown, ChevronUp, CloseIcon } from '../../../assets/icons';

import FloatingLabel from '../../../components/FloatingLabel/FloatingLabel';
import { UploadField } from '../../../components/SharedComponents/FormComponents/UploadField';
import { uploadVerificationDocApi } from '../../../services/authApi/userApi';
import { uploadIdentificationDocuments } from '../../../utils/identificationDocuments';
import { toastNotify } from '../../../utils/toastNotify';
import { useIdentificationUpload } from '../../../Hooks/useIdentificationUpload';

import './KycIdentityUploadModal.scss';

const KycIdentityUploadModal = ({ visible, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [arrowUp, setArrowUp] = useState(false);
  const [form] = Form.useForm();

  const {
    identificationType,
    fileListFront,
    fileListBack,
    fileListPassport,
    fileValidate,
    fileSizeValidate,
    fileFrontValidate,
    fileFrontSizeValidate,
    fileBackValidate,
    fileBackSizeValidate,
    onChangeFront,
    onChangeBack,
    onChangePassport,
    beforeUpload,
    handleRemoveUploaded,
    handleIdentificationChange,
    resetIdentificationState,
    buildUploadsData,
    isUploadInvalid,
    isUploadIncomplete,
  } = useIdentificationUpload();

  const handleClose = () => {
    form.resetFields();
    resetIdentificationState();
    onClose();
  };

  const onSubmit = async () => {
    setLoading(true);

    try {
      const consolidatedDocs = await uploadIdentificationDocuments(buildUploadsData());

      if (Object.keys(consolidatedDocs).length > 0) {
        const uploadResponse = await uploadVerificationDocApi(consolidatedDocs);
        if (uploadResponse?.isError) {
          toastNotify('error', uploadResponse?.message || 'Failed to save verification documents');
          return;
        }
        toastNotify('success', uploadResponse?.message || 'Identity documents uploaded successfully');
        handleClose();
        onSuccess();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in upload process:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal className='namechangemodal' open={visible} onCancel={handleClose} closeIcon={<CloseIcon />} footer={false} width={524} centered>
      <h4 className='mb-32 text-center'>Identity Verification Required</h4>
      <p className='mb-24'>
        In order to complete your account verification we are required by federal law to verify your identity.
      </p>
      <hr className='mb-24' />
      <p className='mb-24'>Please upload a valid government issued ID, must be clear and legible. (JPEG, JPG, PNG files only)</p>
      <Form className='namechangeform' name='kycidentityform' form={form} onFinish={onSubmit}>
        <FloatingLabel label='Type of identification' inputId='identification' inputValue={identificationType}>
          <>
            <div id='kyc-identification-anchor' />
            <Form.Item name='identification' rules={[{ required: true, message: 'Please select identification type' }]}>
              <Select
                id='identification'
                onChange={handleIdentificationChange}
                value={identificationType}
                getPopupContainer={() => document.getElementById('kyc-identification-anchor')}
                onDropdownVisibleChange={() => setArrowUp(!arrowUp)}
                suffixIcon={arrowUp ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
              >
                <Select.Option value='passport'>Passport</Select.Option>
                <Select.Option value='license'>ID</Select.Option>
              </Select>
            </Form.Item>
          </>
        </FloatingLabel>
        {identificationType === 'license' && (
          <Row gutter={16}>
            <Col span={24} md={12}>
              <Form.Item className='license-front'>
                <UploadField
                  onChangeHandle={onChangeFront}
                  fileList={fileListFront}
                  id='licenseFront'
                  beforeUpload={(value) => beforeUpload(value, 'front')}
                  handleRemoveUploaded={() => handleRemoveUploaded(fileListFront[0].uid, 'front')}
                  label={'Upload front of ID'}
                  fileValidate={fileFrontValidate}
                  fileSizeValidate={fileFrontSizeValidate}
                  loading={loading}
                />
              </Form.Item>
            </Col>
            <Col span={24} md={12}>
              <Form.Item className='license-back'>
                <UploadField
                  onChangeHandle={onChangeBack}
                  fileList={fileListBack}
                  id='licenseBack'
                  beforeUpload={(value) => beforeUpload(value, 'back')}
                  handleRemoveUploaded={() => handleRemoveUploaded(fileListBack[0].uid, 'back')}
                  label={'Upload back of ID'}
                  fileValidate={fileBackValidate}
                  fileSizeValidate={fileBackSizeValidate}
                  loading={loading}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        {identificationType === 'passport' && (
          <Row>
            <Col span={24} md={12}>
              <Form.Item className='passport'>
                <UploadField
                  onChangeHandle={onChangePassport}
                  fileList={fileListPassport}
                  id='passport'
                  beforeUpload={(value) => beforeUpload(value, 'passport')}
                  handleRemoveUploaded={() => !loading && handleRemoveUploaded(fileListPassport[0].uid, 'passport')}
                  label={'Upload ID'}
                  fileValidate={fileValidate}
                  fileSizeValidate={fileSizeValidate}
                  loading={loading}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        <p className='fz-12 text-taupe-80 mb-32'>
          Still have questions?{' '}
          <Link className='underline fw-600 text-taupe-80' to='/account/help-center'>
            Contact Realbricks Customer Support
          </Link>
        </p>
        <Form.Item className='mb-0'>
          <Button className='w-100' type='primary' htmlType='submit' loading={loading} disabled={isUploadIncomplete || isUploadInvalid}>
            Confirm
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

KycIdentityUploadModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default KycIdentityUploadModal;
